import { useAtom, useSetAtom } from "jotai"
import { twJoin } from "tailwind-merge"
import { deleteConfirmationAtom, userAtom, planTokenAtom, planAtom, roleAtom } from "../state"
import api from "../services/api"
import { INVITE_STATUS, INVITE_STATUS_LABELS } from "../constants/inviteStatus"
import { PLAN_USER_ROLE } from "../constants/planUserRole"
import { useEffect, useState } from "react"
import { Formik } from "formik"
import * as Yup from "yup"
import FormikField from "./Forms/FormikField"
import TextField from "./Forms/FieldTypes/TextField"
import Button from "./Button"
import Spinner from "./Spinner"
import { cn } from "../lib/utils"
import Tooltip from "./Tooltip"
import { PLAN_STATUS } from "../constants/planStatus"
import { useOutletContextPlan } from "../hooks/useOutletContextPlan"

const migrationSchema = Yup.object().shape({
  first_name: Yup.string().required("Required"),
  last_name: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
})

function MentorPreview({
  data,
  theme,
  deletable = false,
  onDelete,
  isEditing = false,
  planToken: planTokenProp,
  onMigrate,
  onInvite,
  inlineEditEnabled = false,
  onUpdate,
  canInvite,
}) {
  const [planToken] = useAtom(planTokenAtom)
  const [deleteConfirmation, setDeleteConfirmation] = useAtom(deleteConfirmationAtom)
  const setPlan = useSetAtom(planAtom)
  const [expandedLegacyId, setExpandedLegacyId] = useState(null)
  const [isMigrating, setIsMigrating] = useState(false)
  const [migrationError, setMigrationError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [invitingId, setInvitingId] = useState(null)
  const [resendCooldowns, setResendCooldowns] = useState({}) // { mentorId: expiryTimestamp }
  const [resendErrors, setResendErrors] = useState({}) // { mentorId: errorMessage }
  const [cooldownTick, setCooldownTick] = useState(0) // increments every second to drive countdown display
  const [editingMentorId, setEditingMentorId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [editError, setEditError] = useState(null)
  const [migratedIds, setMigratedIds] = useState(new Set())
  const { currentStep } = useOutletContextPlan()

  const actualPlanToken = planTokenProp || planToken
  const [role] = useAtom(roleAtom)

  // Tick every second while cooldowns are active so the countdown display stays current
  useEffect(() => {
    const hasActive = Object.values(resendCooldowns).some(exp => exp > Date.now())
    if (!hasActive) return

    const interval = setInterval(() => {
      setCooldownTick(t => t + 1)
      setResendCooldowns(prev => {
        const now = Date.now()
        const updated = Object.fromEntries(
          Object.entries(prev).filter(([, exp]) => exp > now)
        )
        return Object.keys(updated).length !== Object.keys(prev).length ? updated : prev
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [resendCooldowns])

  let border = "border-brand"
  if (theme === "circle") {
    border = "border-circle"
  }

  const onDeleteClick = userId => {
    setDeleteConfirmation(() => async () => {
      await api.removeMentor(planToken, userId)
      if (onDelete) {
        await onDelete()
      }
    })
  }

  const onDeleteLegacyClick = member => {
    setDeleteConfirmation(() => async () => {
      await api.deletePlanContent(member.id)
      if (onMigrate) {
        await onMigrate(member)
      }
    })
  }

  const onResendClick = async (mentor) => {
    setIsSubmitting(true)
    setInvitingId(mentor.id)
    setResendErrors(prev => ({ ...prev, [mentor.id]: null }))
    try {
      await api.inviteMentor(actualPlanToken, mentor.id)
      setResendCooldowns(prev => ({ ...prev, [mentor.id]: Date.now() + 120_000 }))
      if (onInvite) await onInvite()
    } catch (err) {
      setResendErrors(prev => ({
        ...prev,
        [mentor.id]: err.response?.data?.message || "Failed to resend invite",
      }))
    } finally {
      setIsSubmitting(false)
      setInvitingId(null)
    }
  }

  const onInviteClick = async (mentor) => {
    setIsSubmitting(true)
    setInvitingId(mentor.id)
    const data = {
      mentors: [
        {
          email: mentor.email,
          first_name: mentor.first_name,
          last_name: mentor.last_name,
        },
      ],
    }
    try {
      const response = await api.inviteMentors(actualPlanToken, data)
      const newStatus = response.data?.plan_status
      if (newStatus) {
        setPlan(prev => ({ ...prev, status: newStatus }))
      }
      if (onInvite) {
        await onInvite()
      }
    } finally {
      setIsSubmitting(false)
      setInvitingId(null)
    }
  }

  // Handle both data formats
  let mentors = []
  let legacyMembers = []
  let totalCount = 0

  if (Array.isArray(data)) {
    // Old format: just array of mentors
    mentors = data || []
    totalCount = mentors.length
  } else if (data && typeof data === "object") {
    // New format: object with mentors and legacyMembers
    mentors = data.mentors || []
    legacyMembers = data.legacyMembers || []
    totalCount = data.totalCount || mentors.length + legacyMembers.length
  }

  const displayMentors = theme ? mentors : mentors.slice(0, 5)
  const displayLegacyMembers = legacyMembers.filter(m => !migratedIds.has(m.id))

  return (
    <div>
      <ul>
        {displayMentors.map(mentor => {
          const canDelete =
            deletable &&
            (mentor.invitation_status === INVITE_STATUS.NOT_INVITED ||
              mentor.invitation_status === INVITE_STATUS.PENDING)
          const isEditingThis = editingMentorId === mentor.id

          return (
            <li key={mentor.id} className="mb-4 flex items-center justify-center gap-4">
              {isEditingThis ? (
                <Formik
                  initialValues={{
                    first_name: mentor.first_name,
                    last_name: mentor.last_name,
                    email: mentor.email,
                  }}
                  validationSchema={migrationSchema}
                  onSubmit={async (values, actions) => {
                    setIsSaving(true)
                    setEditError(null)
                    try {
                      await api.updateMentor(actualPlanToken, mentor.id, {
                        first_name: values.first_name,
                        last_name: values.last_name,
                        email: values.email,
                      })
                      if (onUpdate) await onUpdate()
                      setEditingMentorId(null)
                    } catch (error) {
                      setEditError(
                        error.response?.data?.message || error.message || "Failed to save changes",
                      )
                      actions.setSubmitting(false)
                    } finally {
                      setIsSaving(false)
                    }
                  }}
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  {({ submitForm, validateForm }) => (
                    <div className={cn("flex-grow rounded-md border p-4 bg-white", border)}>
                      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <FormikField name="first_name" title="First Name" containerClassName="mb-0">
                          <TextField
                            name="first_name"
                            placeholder="First name"
                            type="text"
                            className="w-full"
                          />
                        </FormikField>
                        <FormikField name="last_name" title="Last Name" containerClassName="mb-0">
                          <TextField
                            name="last_name"
                            placeholder="Last name"
                            type="text"
                            className="w-full"
                          />
                        </FormikField>
                        <FormikField name="email" title="Email Address" containerClassName="mb-0">
                          <TextField
                            name="email"
                            placeholder="Email address"
                            type="email"
                            className="w-full"
                          />
                        </FormikField>
                      </div>
                      {editError && (
                        <div className="mb-3 rounded border border-red-300 bg-red-100 p-2 text-sm text-red-700">
                          {editError}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          bgColor="bg-circle hover:bg-circle/90"
                          disabled={isSaving}
                          onClick={async e => {
                            e.preventDefault()
                            const errors = await validateForm()
                            if (Object.keys(errors).length === 0) submitForm()
                          }}
                        >
                          {isSaving ? (
                            <div className="flex items-center gap-2">
                              <Spinner size="sm" />
                              <span>Saving...</span>
                            </div>
                          ) : (
                            "Save"
                          )}
                        </Button>
                        <Button
                          type="button"
                          className="rounded bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
                          disabled={isSaving}
                          onClick={() => {
                            setEditingMentorId(null)
                            setEditError(null)
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </Formik>
              ) : (
                <>
                  <div
                    className={cn(
                      "flex items-center gap-2 rounded-md border border-b px-4 py-2 bg-white",
                      border,
                      isEditing ? "flex-grow" : "w-full",
                    )}
                  >
                    <div
                      className={cn(
                        "flex w-full flex-col gap-1 font-bold text-black",
                        isEditing && "flex-grow",
                      )}
                    >
                      <span className="flex-grow overflow-hidden text-ellipsis">
                        {mentor.first_name} {mentor.last_name}
                      </span>
                      <span className="text-ellipsis text-sm font-normal text-gray-600 overflow-hidden">
                        {mentor.email}
                      </span>
                      {mentor.invitation_status &&
                        mentor.invitation_status !== INVITE_STATUS.NOT_INVITED && (
                          <span className="text-xs text-gray-500">
                            Status: {INVITE_STATUS_LABELS[mentor.invitation_status]}
                          </span>
                        )}
                    </div>
                    {mentor.invitation_status &&
                      (isEditing || canInvite) &&
                      role !== PLAN_USER_ROLE.MENTOR &&
                      mentor.invitation_status === INVITE_STATUS.NOT_INVITED &&
                      currentStep !== PLAN_STATUS.CHOOSE_GUIDE &&
                      currentStep !== PLAN_STATUS.INVITE_STUDENT && (
                        <Tooltip text={"Send an email invitation to this mentor"}>
                          <Button
                            bgColor="bg-circle hover:bg-circle/90"
                            padding="px-2 py-1"
                            className="text-sm"
                            onClick={() => onInviteClick(mentor)}
                            disabled={isSubmitting}
                            type={"button"}
                          >
                            {isSubmitting && invitingId === mentor.id && (
                              <Spinner size="xs" className="mr-1" />
                            )}
                            <i className="fa-solid fa-paper-plane mr-1"></i>
                            {isSubmitting && invitingId === mentor.id ? "Sending..." : "Invite"}
                          </Button>
                        </Tooltip>
                      )}
                    {mentor.invitation_status &&
                      (isEditing || canInvite) &&
                      role !== PLAN_USER_ROLE.MENTOR &&
                      (mentor.invitation_status === INVITE_STATUS.PENDING ||
                        mentor.invitation_status === INVITE_STATUS.CANCELLED) &&
                      currentStep !== PLAN_STATUS.CHOOSE_GUIDE &&
                      currentStep !== PLAN_STATUS.INVITE_STUDENT && (() => {
                        const cooldownEnd = resendCooldowns[mentor.id]
                        const isInCooldown = cooldownEnd && cooldownEnd > Date.now()
                        const secondsLeft = isInCooldown ? Math.ceil((cooldownEnd - Date.now()) / 1000) : 0
                        const resendError = resendErrors[mentor.id]
                        return (
                          <div className="flex flex-col items-end gap-1">
                            <Tooltip
                              placement="bottom-end"
                              text={
                                isInCooldown
                                  ? `You can resend in ${secondsLeft}s`
                                  : "Resend the email invitation to this mentor"
                              }
                            >
                              <Button
                                bgColor="bg-circle hover:bg-circle/90"
                                padding="px-2 py-1"
                                className="text-sm"
                                onClick={() => onResendClick(mentor)}
                                disabled={isSubmitting || isInCooldown}
                                type="button"
                              >
                                {isSubmitting && invitingId === mentor.id ? (
                                  <>
                                    <Spinner size="xs" className="mr-1" />
                                    Sending...
                                  </>
                                ) : isInCooldown ? (
                                  `Resend (${secondsLeft}s)`
                                ) : (
                                  <>
                                    <i className="fa-solid fa-paper-plane mr-1"></i>
                                    Resend
                                  </>
                                )}
                              </Button>
                            </Tooltip>
                            {resendError && (
                              <span className="text-xs text-red-600">{resendError}</span>
                            )}
                          </div>
                        )
                      })()}
                  </div>
                  {inlineEditEnabled && mentor.invitation_status !== INVITE_STATUS.ACCEPTED && (
                    <i
                      onClick={() => {
                        setEditingMentorId(mentor.id)
                        setEditError(null)
                      }}
                      className="fa-solid fa-pen cursor-pointer text-neutral-400 hover:text-circle"
                    />
                  )}
                  {canDelete && (
                    <i
                      onClick={() => onDeleteClick(mentor.id)}
                      className="fa-solid fa-square-minus mt-0.5 cursor-pointer text-neutral-400"
                    />
                  )}
                </>
              )}
            </li>
          )
        })}
        {/* Show legacy members in preview */}
        {displayLegacyMembers.map(member => {
          const isExpanded = expandedLegacyId === member.id

          return (
            <li key={`legacy-${member.id}`} className="mb-4 flex items-center justify-center gap-4">
              {!(canInvite && role !== PLAN_USER_ROLE.MENTOR) ? (
                <div className="flex w-full items-center justify-center gap-4 opacity-70">
                  <div
                    className={twJoin(
                      "flex w-full flex-col gap-1 rounded-md border border-b bg-yellow-50 px-4 py-2 font-bold text-black",
                      "border-yellow-300",
                    )}
                  >
                    <span className="overflow-hidden text-ellipsis">{member.legacy_name}</span>
                    <span className="text-xs text-yellow-700">
                      <i className="fa-solid fa-triangle-exclamation mr-1"></i>
                      Missing email
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <div className={cn("flex-grow rounded border border-yellow-400 bg-white")}>
                    <div
                      className="flex cursor-pointer items-center justify-between p-3 hover:bg-yellow-100"
                      onClick={e => {
                        e.stopPropagation()
                        setExpandedLegacyId(isExpanded ? null : member.id)
                        setMigrationError(null)
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <i className="fa-solid fa-user-clock text-yellow-600"></i>
                        <div>
                          <div className="font-bold text-gray-900">{member.legacy_name}</div>
                          <div className="text-xs text-gray-600">
                            Click to {isExpanded ? "collapse" : "add email"}
                          </div>
                        </div>
                      </div>
                      <i
                        className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"} text-gray-400`}
                      ></i>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-yellow-300 bg-gray-50 p-4">
                        <Formik
                          initialValues={{
                            first_name: member.first_name,
                            last_name: member.last_name,
                            email: member.email,
                          }}
                          validationSchema={migrationSchema}
                          onSubmit={async (values, actions) => {
                            setIsMigrating(true)
                            setMigrationError(null)

                            try {
                              console.log("Starting migration for:", values)

                              // Step 1: Add mentor in new format
                              await api.addMentors(actualPlanToken, [
                                {
                                  first_name: values.first_name,
                                  last_name: values.last_name,
                                  email: values.email,
                                },
                              ])

                              // Step 2: Delete legacy PlanContent record
                              await api.deletePlanContent(member.id)

                              // Hide immediately so the card doesn't linger while parent refreshes
                              setMigratedIds(prev => new Set([...prev, member.id]))

                              // Step 3: Wait for data refresh to complete
                              if (onMigrate) {
                                await onMigrate(member)
                              }

                              actions.resetForm()
                              setIsMigrating(false)
                              setExpandedLegacyId(null)
                            } catch (error) {
                              console.error("Migration error:", error)
                              console.error("Error details:", error.response?.data)
                              setMigrationError(
                                error.response?.data?.message ||
                                  error.message ||
                                  "Failed to migrate mentor",
                              )
                              setIsMigrating(false)
                              actions.setSubmitting(false)
                            }
                          }}
                          validateOnChange={false}
                          validateOnBlur={false}
                        >
                          {({ submitForm, validateForm }) => (
                            <div>
                              <p className="mb-4 text-sm text-gray-700">
                                Review and update the name if needed, then add an email address:
                              </p>

                              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                                <FormikField
                                  name="first_name"
                                  title="First Name"
                                  containerClassName="mb-0"
                                >
                                  <TextField
                                    name="first_name"
                                    placeholder="First name"
                                    type="text"
                                    className="w-full"
                                  />
                                </FormikField>

                                <FormikField
                                  name="last_name"
                                  title="Last Name"
                                  containerClassName="mb-0"
                                >
                                  <TextField
                                    name="last_name"
                                    placeholder="Last name"
                                    type="text"
                                    className="w-full"
                                  />
                                </FormikField>

                                <FormikField
                                  name="email"
                                  title="Email Address"
                                  containerClassName="mb-0"
                                >
                                  <TextField
                                    name="email"
                                    placeholder="mentor@example.com"
                                    type="email"
                                    className="w-full"
                                  />
                                </FormikField>
                              </div>

                              {migrationError && (
                                <div className="mb-3 rounded border border-red-300 bg-red-100 p-2 text-sm text-red-700">
                                  {migrationError}
                                </div>
                              )}

                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  bgColor="bg-circle hover:bg-circle/90"
                                  disabled={isMigrating}
                                  onClick={async e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    const errors = await validateForm()
                                    if (Object.keys(errors).length === 0) {
                                      submitForm()
                                    }
                                  }}
                                >
                                  {isMigrating ? (
                                    <div className="flex items-center gap-2">
                                      <Spinner size="sm" />
                                      <span>Saving...</span>
                                    </div>
                                  ) : (
                                    <>Save</>
                                  )}
                                </Button>

                                <Button
                                  type="button"
                                  className="rounded bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
                                  onClick={e => {
                                    e.stopPropagation()
                                    setExpandedLegacyId(null)
                                    setMigrationError(null)
                                  }}
                                  disabled={isMigrating}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </Formik>
                      </div>
                    )}
                  </div>

                  <i
                    onClick={e => {
                      e.stopPropagation()
                      onDeleteLegacyClick(member)
                    }}
                    className="fa-solid fa-square-minus mt-0.5 cursor-pointer text-neutral-400 hover:text-red-600"
                  />
                </>
              )}
            </li>
          )
        })}
      </ul>
      {!theme && totalCount > 5 && <div className="font-bold">+{totalCount - 5} more</div>}
    </div>
  )
}

export default MentorPreview
